import { apiBase } from '../lib/apiBase';
import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Download, Palmtree, Plus } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { useI18n } from '../contexts/I18nContext';
import { usePermissions } from '../hooks/usePermissions';
import { useUsers } from '../hooks/useUsers';
import {
  useCreateLeaveRequest,
  useLeaveBalance,
  useLeaveReport,
  useLeaveRequests,
  useUpdateLeaveRequest,
} from '../hooks/useLeave';
import {
  leaveReportCsvPath,
  type LeaveFilters,
  type LeaveRequest,
  type LeaveStatus,
  type LeaveType,
} from '../lib/leaveApi';
import { LEAVE_STATUSES, LEAVE_TYPES } from '../lib/leaveConstants';
import { useAuthStore } from '../store/useAuthStore';
import { LeaveBalanceCard } from '../components/leave/LeaveBalanceCard';
import { LeaveForm, type LeaveFormValues } from '../components/leave/LeaveForm';
import { LeaveRequestsTable } from '../components/leave/LeaveRequestsTable';
import { Select } from '../components/ui/Select';
import { FadeIn, SkeletonTable } from '../components/ui/Skeleton';
import { ToastProvider, useToast } from '../components/tasks/linear/Toast';

type Tab = 'mine' | 'team' | 'report';

/**
 * The whole team's requests fit in one page; there is no pager in this UI.
 * 200 is the cap the API enforces (leave.controller.ts) — asking for more is
 * a 400, not a bigger page.
 */
const TEAM_PAGE_SIZE = 200;

/**
 * "Concedii" — leave requests.
 *
 * Three tabs, two of them gated: filing and reading YOUR OWN leave is an
 * auth-only feature (there is deliberately no `leave.canView` flag), while the
 * team list and the yearly report need `leave.canViewAll`.
 *
 * The page mounts its own `ToastProvider` (same as `TasksView`) because the
 * app has no global one, and approving or withdrawing a request needs to
 * confirm itself somewhere other than the row that just disappeared.
 */
export function LeavePage() {
  return (
    <ToastProvider>
      <LeavePageBody />
    </ToastProvider>
  );
}

function LeavePageBody() {
  const { t } = useI18n();
  const { can } = usePermissions();
  const canViewAll = can('leave', 'canViewAll');
  const canManage = can('leave', 'canManage');
  const myUserId = useAuthStore((s) => s.user?.id);

  const [searchParams, setSearchParams] = useSearchParams();
  // The notification emails and the push payload both link to
  // `/concedii?cerere=<id>`.
  const deepLinkId = searchParams.get('cerere');

  const [tab, setTab] = useState<Tab>('mine');
  const [selectedId, setSelectedId] = useState<string | null>(deepLinkId);

  const mine = useLeaveRequests(
    useMemo<LeaveFilters>(() => ({ userId: myUserId }), [myUserId]),
    Boolean(myUserId),
  );
  const myRequests = mine.data?.requests ?? [];

  // Resolve the deep link once the caller's own requests are in: a row that is
  // not theirs can only live on the team tab, so open that one instead.
  const deepLinkHandled = useRef(false);
  useEffect(() => {
    if (!deepLinkId || deepLinkHandled.current || mine.isLoading) return;
    deepLinkHandled.current = true;
    const isMine = myRequests.some((r) => r.id === deepLinkId);
    if (!isMine && canViewAll) setTab('team');
  }, [deepLinkId, mine.isLoading, myRequests, canViewAll]);

  // Permissions arrive after the first render; a link to the team tab from
  // someone who turns out not to have access must fall back, not blank out.
  useEffect(() => {
    if (!canViewAll && tab !== 'mine') setTab('mine');
  }, [canViewAll, tab]);

  function selectRequest(id: string | null) {
    setSelectedId(id);
    // Keep the URL honest so the row survives a refresh and can be re-shared.
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        if (id) params.set('cerere', id);
        else params.delete('cerere');
        return params;
      },
      { replace: true },
    );
  }

  const tabs: { id: Tab; label: string; visible: boolean }[] = [
    { id: 'mine', label: t('leave.tab.mine'), visible: true },
    { id: 'team', label: t('leave.tab.team'), visible: canViewAll },
    { id: 'report', label: t('leave.tab.report'), visible: canViewAll },
  ];

  return (
    <div className="w-full p-4 sm:p-6 lg:p-8">
      <div className="flex items-center gap-2">
        <Palmtree className="text-brand-600" size={24} />
        <h1 className="text-2xl font-bold text-foreground">{t('leave.title')}</h1>
      </div>
      <p className="mt-1 text-sm text-muted-foreground">{t('leave.subtitle')}</p>

      <div
        role="tablist"
        aria-label={t('leave.title')}
        className="mt-6 flex gap-1 overflow-x-auto border-b border-border"
      >
        {tabs
          .filter((entry) => entry.visible)
          .map((entry) => (
            <button
              key={entry.id}
              type="button"
              role="tab"
              aria-selected={tab === entry.id}
              onClick={() => setTab(entry.id)}
              className={`ui-tab ${tab === entry.id ? 'ui-tab-active' : ''}`}
            >
              {entry.label}
            </button>
          ))}
      </div>

      <div className="mt-6">
        {tab === 'mine' && (
          <MyLeaveTab
            requests={myRequests}
            isLoading={mine.isLoading}
            isError={mine.isError}
            canManage={canManage}
            selectedId={selectedId}
            onSelect={selectRequest}
          />
        )}
        {tab === 'team' && canViewAll && (
          <TeamLeaveTab myUserId={myUserId} selectedId={selectedId} onSelect={selectRequest} />
        )}
        {tab === 'report' && canViewAll && <LeaveReportTab />}
      </div>
    </div>
  );
}

// --- "Cererile mele" --------------------------------------------------------

function MyLeaveTab({
  requests,
  isLoading,
  isError,
  canManage,
  selectedId,
  onSelect,
}: {
  requests: LeaveRequest[];
  isLoading: boolean;
  isError: boolean;
  canManage: boolean;
  selectedId: string | null;
  onSelect: (id: string | null) => void;
}) {
  const { t } = useI18n();
  const toast = useToast();
  const balance = useLeaveBalance();
  const create = useCreateLeaveRequest();
  const update = useUpdateLeaveRequest();
  const users = useUsers();

  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<LeaveRequest | null>(null);

  // Filing for a colleague is a `leave.canManage` power; everyone else only
  // ever files for themselves, so the person field is not even rendered.
  const people = canManage
    ? (users.data ?? []).map((u) => ({ id: u.id, name: u.name }))
    : undefined;

  async function submit(values: LeaveFormValues) {
    try {
      // The toast reports the status the server came back with, rather than
      // "saved": what a person wants confirmed is that the request is now
      // waiting for a decision.
      let saved: LeaveRequest;
      if (editing) {
        saved = await update.mutateAsync({
          id: editing.id,
          patch: {
            type: values.type,
            startDate: values.startDate,
            endDate: values.endDate,
            hours: values.hours,
            reason: values.reason,
          },
        });
        setEditing(null);
      } else {
        saved = await create.mutateAsync(values);
        setAdding(false);
      }
      toast.show(t(`leave.status.${saved.status}`));
    } catch (err) {
      toast.show(err instanceof Error ? err.message : t('common.error'));
    }
  }

  return (
    <FadeIn className="flex flex-col gap-4">
      <LeaveBalanceCard balance={balance.data} isLoading={balance.isLoading} />

      <div className="rounded-xl border border-border bg-card">
        {adding || editing ? (
          <div className="border-b border-border bg-accent/30">
            <LeaveForm
              // A key per edited row remounts the form, so switching rows
              // reloads its values instead of keeping the previous ones.
              key={editing?.id ?? 'new'}
              request={editing ?? undefined}
              people={editing ? undefined : people}
              busy={create.isPending || update.isPending}
              onCancel={() => {
                setAdding(false);
                setEditing(null);
              }}
              onSave={(values) => void submit(values)}
            />
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setAdding(true)}
            className="flex w-full items-center gap-3 border-b border-border px-4 py-3 text-left text-[14px] text-primary hover:bg-accent/50"
          >
            <Plus size={18} />
            <span>{t('leave.new')}</span>
          </button>
        )}

        <div className="p-3">
          {isLoading && <SkeletonTable rows={4} columns={5} />}
          {isError && <p className="py-6 text-center text-sm text-red-600">{t('leave.error')}</p>}
          {!isLoading && !isError && (
            <LeaveRequestsTable
              requests={requests}
              selectedId={selectedId}
              onEdit={setEditing}
              onSelect={(request) => {
                onSelect(request.id);
                // Clicking a row you may still change opens it for editing;
                // a decided request is read-only, so it is only highlighted.
                if (request.permissions.canEdit) setEditing(request);
              }}
            />
          )}
        </div>
      </div>
    </FadeIn>
  );
}

// --- "Toate cererile" -------------------------------------------------------

function TeamLeaveTab({
  myUserId,
  selectedId,
  onSelect,
}: {
  /** The viewer's own requests are left out: this list is what they decide on. */
  myUserId: string | undefined;
  selectedId: string | null;
  onSelect: (id: string | null) => void;
}) {
  const { t } = useI18n();
  const users = useUsers();
  const [status, setStatus] = useState<LeaveStatus | ''>('');
  const [type, setType] = useState<LeaveType | ''>('');
  const [userId, setUserId] = useState('');

  const filters = useMemo<LeaveFilters>(
    () => ({
      ...(status ? { status: [status] } : {}),
      ...(type ? { type: [type] } : {}),
      ...(userId ? { userId } : {}),
      ...(myUserId ? { excludeUserId: myUserId } : {}),
      pageSize: TEAM_PAGE_SIZE,
    }),
    [status, type, userId, myUserId],
  );
  const list = useLeaveRequests(filters);
  const filtered = Boolean(status || type || userId);

  return (
    <FadeIn className="flex flex-col gap-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Select
          label={t('leave.filter.status')}
          value={status}
          onChange={(next) => setStatus(next as LeaveStatus | '')}
          clearable
          placeholder={t('leave.filter.all')}
          options={LEAVE_STATUSES.map((value) => ({
            value,
            label: t(`leave.status.${value}`),
          }))}
        />
        <Select
          label={t('leave.filter.type')}
          value={type}
          onChange={(next) => setType(next as LeaveType | '')}
          clearable
          placeholder={t('leave.filter.all')}
          options={LEAVE_TYPES.map((value) => ({ value, label: t(`leave.type.${value}`) }))}
        />
        <Select
          label={t('leave.filter.person')}
          value={userId}
          onChange={setUserId}
          clearable
          placeholder={t('leave.filter.all')}
          loading={users.isLoading}
          options={(users.data ?? [])
            .filter((u) => u.id !== myUserId)
            .map((u) => ({ value: u.id, label: u.name }))}
        />
      </div>

      {list.isLoading && <SkeletonTable rows={6} columns={6} />}
      {list.isError && <p className="py-6 text-center text-sm text-red-600">{t('leave.error')}</p>}
      {!list.isLoading && !list.isError && (
        <LeaveRequestsTable
          requests={list.data?.requests ?? []}
          showPerson
          filtered={filtered}
          emptyLabel={t('leave.emptyTeam')}
          selectedId={selectedId}
          onSelect={(request) => onSelect(request.id)}
        />
      )}
    </FadeIn>
  );
}

// --- "Raport" ---------------------------------------------------------------

function LeaveReportTab() {
  const { t } = useI18n();
  const toast = useToast();
  const [year, setYear] = useState(() => new Date().getFullYear());
  const report = useLeaveReport(year);
  const [exporting, setExporting] = useState(false);

  /**
   * The CSV is a streamed attachment behind the same bearer token as the rest
   * of the API, so it cannot be a plain `<a href>` — the browser would send no
   * Authorization header. Fetch the bytes, then hand them to a throwaway link.
   */
  async function downloadCsv() {
    setExporting(true);
    try {
      const token = useAuthStore.getState().accessToken;
      const res = await fetch(`${apiBase()}${leaveReportCsvPath(year)}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error(t('leave.error'));
      const url = URL.createObjectURL(await res.blob());
      const a = document.createElement('a');
      a.href = url;
      a.download = `concedii-${year}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.show(err instanceof Error ? err.message : t('common.error'));
    } finally {
      setExporting(false);
    }
  }

  const rows = report.data?.rows ?? [];

  return (
    <FadeIn className="flex flex-col gap-4">
      {/* One row on every width: the year stepper as a single control on the
          left, the export on the right. On a phone the tab already says
          "Raport", so the stepper shows just the year. */}
      <div className="flex items-center justify-between gap-3">
        <div className="inline-flex min-w-0 items-center rounded-lg border border-border bg-card">
          <button
            type="button"
            onClick={() => setYear((y) => y - 1)}
            aria-label={t('common.previous')}
            className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-l-lg text-muted-foreground hover:bg-accent hover:text-foreground active:bg-accent"
          >
            <ChevronLeft size={16} />
          </button>
          <h2
            className="truncate px-1.5 font-heading text-sm font-semibold tabular-nums text-foreground sm:px-2 sm:text-base"
            aria-label={t('leave.report.title', { year })}
          >
            <span className="sm:hidden">{year}</span>
            <span className="hidden sm:inline">{t('leave.report.title', { year })}</span>
          </h2>
          <button
            type="button"
            onClick={() => setYear((y) => y + 1)}
            aria-label={t('common.next')}
            className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-r-lg text-muted-foreground hover:bg-accent hover:text-foreground active:bg-accent"
          >
            <ChevronRight size={16} />
          </button>
        </div>
        <button
          type="button"
          onClick={() => void downloadCsv()}
          disabled={exporting || rows.length === 0}
          className="ui-btn-primary shrink-0"
        >
          <Download size={14} />
          {t('leave.report.export')}
        </button>
      </div>

      {report.isLoading && <SkeletonTable rows={6} columns={5} />}
      {report.isError && (
        <p className="py-6 text-center text-sm text-red-600">{t('leave.noAccess')}</p>
      )}
      {!report.isLoading && !report.isError && rows.length === 0 && (
        <p className="py-8 text-center text-sm text-muted-foreground">{t('leave.report.empty')}</p>
      )}
      {/* Below lg (phones, and tablets where the sidebar eats half the width):
          one card per person instead of a five-column table that only
          scrolled sideways. What is left is the number that matters,
          so it gets the right-hand spot; the rest sits underneath. */}
      {!report.isLoading && !report.isError && rows.length > 0 && (
        <ul className="divide-y divide-border overflow-hidden rounded-lg border border-border bg-card lg:hidden">
          {rows.map((row) => (
            <li key={row.userId} className="px-3 py-2.5">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium text-foreground">{row.name}</div>
                  <div className="truncate text-xs text-muted-foreground">{row.email}</div>
                </div>
                <div className="shrink-0 text-right">
                  <div className="text-base font-semibold leading-5 tabular-nums text-foreground">
                    {row.balance.remainingDays}
                  </div>
                  <div className="text-[11px] text-muted-foreground">
                    {t('leave.balance.remaining')}
                  </div>
                </div>
              </div>
              <dl className="mt-2 grid grid-cols-3 gap-2 text-xs">
                <div>
                  <dt className="text-muted-foreground">{t('leave.balance.entitled')}</dt>
                  <dd className="tabular-nums text-foreground">
                    {row.balance.entitledDays}
                    {row.balance.isDefaultEntitlement && (
                      <span title={t('leave.balance.default')}> *</span>
                    )}
                  </dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">{t('leave.balance.used')}</dt>
                  <dd className="tabular-nums text-foreground">{row.balance.usedDays}</dd>
                </div>
                <div>
                  <dt className="text-muted-foreground">{t('leave.balance.pending')}</dt>
                  <dd className="tabular-nums text-foreground">{row.balance.pendingDays}</dd>
                </div>
              </dl>
            </li>
          ))}
        </ul>
      )}
      {!report.isLoading && !report.isError && rows.length > 0 && (
        <div className="hidden overflow-x-auto rounded-lg border border-border lg:block">
          <table className="min-w-full divide-y divide-border text-sm">
            <thead className="bg-accent/40">
              <tr>
                <th className="px-3 py-2 text-left font-medium text-gray-600">
                  {t('leave.column.person')}
                </th>
                <th className="px-3 py-2 text-right font-medium text-gray-600">
                  {t('leave.balance.entitled')}
                </th>
                <th className="px-3 py-2 text-right font-medium text-gray-600">
                  {t('leave.balance.used')}
                </th>
                <th className="px-3 py-2 text-right font-medium text-gray-600">
                  {t('leave.balance.pending')}
                </th>
                <th className="px-3 py-2 text-right font-medium text-gray-600">
                  {t('leave.balance.remaining')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border bg-card">
              {rows.map((row) => (
                <tr key={row.userId}>
                  <td className="px-3 py-2">
                    <div className="text-foreground">{row.name}</div>
                    <div className="text-xs text-muted-foreground">{row.email}</div>
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 text-right text-muted-foreground">
                    {row.balance.entitledDays}
                    {/* A star marks the house default, so a manager can tell at
                        a glance whose allowance was never set. */}
                    {row.balance.isDefaultEntitlement && (
                      <span title={t('leave.balance.default')}> *</span>
                    )}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 text-right text-muted-foreground">
                    {row.balance.usedDays}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 text-right text-muted-foreground">
                    {row.balance.pendingDays}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 text-right font-medium text-foreground">
                    {row.balance.remainingDays}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="text-xs text-muted-foreground">{t('leave.balance.hint')}</p>
    </FadeIn>
  );
}
